import { Editor } from '@tiptap/core';
import Heading from '@tiptap/extension-heading';
import { Plugin } from 'prosemirror-state';

export type Level = 1 | 2 | 3 | 4;

interface NumberedHeadingOptions {
  levels: Array<Number>;
  setNewParagraphAlignment: Function;
  getShowNumberedHeadings: Function;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    numberedHeading: {
      setHeading: (attributes: { level: Level }) => ReturnType;
      toggleHeading: (attributes: { level: Level }) => ReturnType;
      updateHeadingNumbers: () => ReturnType;
    };
  }
}

export const NumberedHeading = Heading.extend<NumberedHeadingOptions>({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        default: null,
        parseHTML: element => element.getAttribute('id'),
        renderHTML: attributes => {
          if (!attributes['id']) return {};
          return {
            id: attributes['id']
          };
        }
      },
      'data-show-numbering': {
        default: false,
        parseHTML: element => element.getAttribute('data-show-numbering'),
        renderHTML: attributes => {
          if (!attributes['data-show-numbering']) return {};
          return {
            'data-show-numbering': attributes['data-show-numbering']
          };
        }
      }
    };
  },

  addOptions() {
    return {
      levels: [],
      setNewParagraphAlignment: () => {},
      getShowNumberedHeadings: () => Boolean
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),

      updateHeadingNumbers:
        () =>
        ({ editor }) => {
          setTimeout(() => numberHeadings(editor, this.options.getShowNumberedHeadings()));
          return false;
        }
    };
  },

  addProseMirrorPlugins() {
    const that = this;

    return [
      new Plugin({
        appendTransaction(transactions, oldState, newState) {
          const oldHeadings = getHeadings(oldState.doc);
          const newHeadings = getHeadings(newState.doc);

          if (oldHeadings.join('|') === newHeadings.join('|') && !that.editor.isEmpty) return null;

          setTimeout(() => numberHeadings(that.editor, that.options.getShowNumberedHeadings()), 200);
          return;
        }
      })
    ] as any;
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => this.options.setNewParagraphAlignment()
    };
  }
});

export function getHeadings(doc: any) {
  const headings: string[] = [];
  doc.descendants((node: any) => {
    if (node.type.name === 'heading' && [2, 3, 4].includes(node.attrs.level)) {
      headings.push(node.attrs.id + node.textContent);
    }
  });
  return headings;
}

export function numberHeadings(editor: Editor, showNumberedHeadings: boolean) {
  const levels = [0, 0, 0];

  editor.commands.command(({ tr, state }) => {
    const { doc } = state;
    doc.descendants((node, pos) => {
      if (node.type.name === 'heading') {
        const level = node.attrs.level;
        if (level < 2 || level > 4) return;

        levels[level - 2]++;
        for (let i = level - 1; i < 3; i++) {
          levels[i] = 0;
        }

        const numbering = levels.slice(0, level - 1).join('.') + '.';

        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          'data-show-numbering': showNumberedHeadings,
          id: numbering
        }).setMeta('addToHistory', false);
      }
    });

    return true;
  });
}
